import React, { useEffect } from "react"; 
import { Link } from "react-router-dom"; 
import { Calendar, User, ArrowRight } from "lucide-react";
import "aos/dist/aos.css";
import { Helmet } from "react-helmet-async"; 
import blog from '../Img/blog.jpeg'
import blogimage from '../Img/blog.jpg'

const BlogList = () => {
  useEffect(() => {
    import("aos").then((AOS) => {
      AOS.default.init({ duration: 500, once: true });
    });
  }, []);

  // 🚀 EXPANDED & NATURAL BLOG CONTENT FOR HIGH VALUE ADSENSE APPROVAL
  const blogs = [
    {
      id: 1,
      title: "5 Reasons Why Browser Games Are Better Than Ever",
      excerpt: "Remember the clunky flash games of the 2000s? Things have changed! Discover how modern HTML5 games offer stunning graphics, smooth gameplay, and instant fun without taking up your hard drive space.",
      date: "May 10, 2026",
      author: "Game Zone Editor", 
      image: "https://images.unsplash.com/photo-1552820728-8b83bb6b773f?q=80&w=900&auto=format&fit=crop",
      slug: "reasons-browser-games-are-better"
    },
    {
      id: 2,
      title: "The Ultimate Guide to Winning IO Games",
      excerpt: "Struggling to survive in Snake or Agar.io? We break down the top strategies used by pro players to dominate multiplayer arenas, avoid traps, and quickly climb to the top of the leaderboard.",
      date: "May 02, 2026",
      author: "Pro Gamer Tips",
      image: "https://images.unsplash.com/photo-1542751371-adc38448a05e?q=80&w=900&auto=format&fit=crop",
      slug: "guide-to-winning-io-games"
    },
    {
      id: 3,
      title: "Top 10 Brain-Teasing Puzzle Games to Play on a Break",
      excerpt: "Need a quick mental workout during your coffee break? Here is a curated list of the best logic and puzzle games that are easy to start but will seriously challenge your problem-solving skills.",
      date: "Apr 25, 2026",
      author: "Game Zone Team",
      image: "https://images.unsplash.com/photo-1511512578047-dfb367046420?q=80&w=900&auto=format&fit=crop",
      slug: "top-10-puzzle-games-break"
    },
    {
      id: 4,
      title: "How HTML5 is Changing the Mobile Gaming Industry",
      excerpt: "App stores are crowded, and users are tired of downloading huge files just to try a game. Learn why developers are turning to HTML5 to create games that you can play directly from a link on your phone.",
      date: "Apr 18, 2026",
      author: "Tech Desk",
      image: "https://images.unsplash.com/photo-1550745165-9bc0b252726f?q=80&w=900&auto=format&fit=crop",
      slug: "html5-changing-mobile-gaming"
    },
    {
      id: 5,
      title: "Best Unblocked Games to Play at School or Work",
      excerpt: "Got some free time but facing annoying network restrictions? We have compiled a safe, family-friendly list of the best unblocked games you can enjoy without compromising network security.",
      date: "Apr 10, 2026",
      author: "Gaming Historian",
      image: `${blog}`,
      slug: "best-unblocked-games"
    },
    {
      id: 6,
      title: "Why Casual Gaming is Great for Stress Relief",
      excerpt: "Not all games need to be intense. In fact, playing simple, repetitive casual games like match-3 or farming simulators can significantly lower your stress levels after a long, hard day.",
      date: "Mar 28, 2026",
      author: "Wellness & Tech",
      image: blogimage,
      slug: "casual-gaming-stress-relief"
    },
    {
      id: 7,
      title: "The Rise of Physics-Based Web Games",
      excerpt: "From drawing bridges to balancing monster trucks, physics engines have made web games incredibly fun. We dive into why gravity-based mechanics make these simple games so highly addictive.",
      date: "Mar 15, 2026",
      author: "Game Dev Insights",
      image: "https://images.unsplash.com/photo-1511512578047-dfb367046420?q=80&w=900&auto=format&fit=crop", // Reusing image for demo
      slug: "rise-of-physics-web-games"
    },
    {
      id: 8,
      title: "Fixing Browser Lag: Tips for Smoother Gameplay",
      excerpt: "Is your car stuttering right before the finish line? Don't let a slow browser ruin your fun. Follow these simple steps to clear your cache, enable hardware acceleration, and get buttery-smooth 60 FPS.",
      date: "Mar 05, 2026",
      author: "Tech Support",
      image: "https://images.unsplash.com/photo-1542751371-adc38448a05e?q=80&w=900&auto=format&fit=crop", // Reusing image for demo
      slug: "fixing-browser-lag-tips"
    }
  ];

  const schemaData = {
    "@context": "https://schema.org",
    "@type": "Blog",
    "name": "Game Zone Hub Gaming Blog",
    "description": "Latest news, tips, and guides about playing free online HTML5 browser games.",
    "url": "https://www.gamezonehub.in/",
    "blogPost": blogs.map(blog => ({
      "@type": "BlogPosting",
      "headline": blog.title,
      "image": blog.image,
      "datePublished": new Date(blog.date).toISOString(),
      "author": {
        "@type": "Organization",
        "name": blog.author
      },
      "url": `https://www.gamezonehub.in/blog/${blog.slug}`
    }))
  };

  return (
    <div id="blog-section" className="bg-gradient-to-br from-[#0f172a] via-[#1e293b] to-[#0f172a] sm:pt-[70px] pt-[50px] sm:pb-20 pb-10">
      
      <Helmet>
        {schemaData && (
          <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(schemaData) }} />
        )}
      </Helmet>

      <div className="container mx-auto px-4">
        {/* 🚀 NATURAL HUMAN WRITTEN HEADER TEXT */}
        <div className="text-center mb-12" data-aos="fade-down">
          <h2 className="text-3xl sm:text-5xl font-extrabold text-white mb-4">
            Our Gaming <span className="text-blue-500">Blog</span>
          </h2>
          <p className="text-gray-400 max-w-3xl mx-auto text-lg leading-relaxed">
            Welcome to the Game Zone Hub blog! This is where our team shares tips on how to beat hard levels, lists of our favorite new releases, and deep dives into why we love playing <strong>free online browser games</strong>.
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
          {blogs.map((blog, index) => (
            <article 
              key={blog.id} 
              className="bg-[#1e293b] rounded-2xl overflow-hidden border border-white/5 hover:border-blue-500/30 transition-all duration-300 group hover:-translate-y-2 shadow-lg flex flex-col"
              data-aos="fade-up"
              data-aos-delay={(index % 4) * 100}
            >
              <div className="relative h-40 overflow-hidden shrink-0">
                <img 
                  src={blog.image} 
                  alt={blog.title}
                  title={blog.title}
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                  loading="lazy"
                />
                <div className="absolute top-3 left-3 bg-blue-600/90 backdrop-blur-sm text-white text-[10px] font-bold px-2.5 py-1 rounded-full shadow-md uppercase tracking-wider">
                  Article
                </div>
              </div>
              
              <div className="p-5 flex flex-col flex-grow">
                <div className="flex items-center gap-3 text-[11px] text-gray-400 mb-3 font-medium">
                  <span className="flex items-center gap-1"><Calendar size={12}/> {blog.date}</span>
                  <span className="flex items-center gap-1"><User size={12}/> {blog.author}</span>
                </div>
                
                <h3 className="text-lg font-bold text-white mb-2 line-clamp-2 group-hover:text-blue-400 transition-colors leading-snug">
                  {blog.title}
                </h3>
                
                <p className="text-gray-400 text-sm mb-4 line-clamp-3 flex-grow leading-relaxed">
                  {blog.excerpt}
                </p>
                
                <Link
                  to={`/blog/${blog.slug}`} 
                  className="inline-flex items-center gap-1.5 text-blue-400 font-bold hover:text-blue-300 transition-colors text-sm cursor-pointer mt-auto w-fit group-hover:gap-2"
                  aria-label={`Read full article: ${blog.title}`}
                >
                  Read Post <ArrowRight size={16}/>
                </Link>
              </div>
            </article>
          ))}
        </div>

        {/* 🚀 NATURAL VALUE-ADDED BOTTOM SECTION */}
        <div className="sm:mt-16 mt-10 bg-black/30 p-8 rounded-2xl border border-white/5 text-center" data-aos="fade-up">
            <h3 className="text-2xl font-bold text-white mb-3">Level Up Your Game</h3>
            <p className="text-gray-400 text-sm sm:text-base max-w-4xl mx-auto leading-relaxed">
               We don't just host games; we play them too! Our editorial team is constantly exploring the web to bring you the best strategies, reviews, and gaming news. Whether you are stuck on a tough puzzle or just looking for something new to play without downloading an app, our blog is the perfect place to start reading.
            </p>
        </div>

      </div>
    </div>
  );
};

export default BlogList;